import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdminLoginRequest } from '@naya/shared'
import { adminApi, AdminApiError } from '../adminApi.js'
import type { AdminMe } from '../types.js'

export const adminMeKey = ['admin', 'me'] as const

/**
 * Current admin session. A 401 means "not signed in" and is treated as a
 * resolved `null` rather than an error — the AdminRoute wrapper redirects on
 * null. Any other failure (500, network) propagates as an error so the
 * wrapper can surface it instead of silently bouncing to login.
 */
export function useAdminMe() {
  return useQuery<AdminMe | null>({
    queryKey: adminMeKey,
    retry: false,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        return await adminApi.get<AdminMe>('/auth/me')
      } catch (err) {
        if (err instanceof AdminApiError && err.status === 401) return null
        throw err
      }
    },
  })
}

interface LoginResponse {
  admin: { id: string; email: string; name: string }
  session_expires_at: string
}

export function useAdminLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: AdminLoginRequest) =>
      adminApi.post<LoginResponse>('/auth/login', input),
    // Await the /auth/me refetch before resolving so AdminRoute reads a
    // populated session cache when the page navigates — otherwise it flashes
    // /admin/login before the dashboard.
    onSuccess: async () => {
      await qc.refetchQueries({ queryKey: adminMeKey })
    },
  })
}

export function useAdminLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => adminApi.post<{ ok: true }>('/auth/logout'),
    onSuccess: () => {
      qc.clear()
    },
  })
}

/**
 * Exit impersonation from the admin UI. This is the admin-side counterpart to
 * the owner app's banner — it means the admin is never stranded in an
 * impersonating session (every admin data route 403s while impersonating, with
 * no exit affordance otherwise) and never has to log out / back in to recover.
 *
 * On success we invalidate EVERY query: the session can touch admin routes
 * again, but the data queries that 403'd during impersonation stay errored
 * until forced to refetch — that stale-error state is what makes the admin look
 * "broken" after exiting. Invalidating refetches them all, so the admin is
 * immediately usable in the same tab.
 */
export function useExitImpersonation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      adminApi.post<{ ok: true; duration_ms: number | null }>(
        '/impersonation/exit',
      ),
    onSuccess: async () => {
      await qc.invalidateQueries()
      await qc.refetchQueries({ queryKey: adminMeKey })
    },
  })
}
